/**
 * The sentence-shape rescue ladder (D29): the systemic answer to
 * non-imperative English, run ONLY when a clause has already defeated the
 * imperative grammar.
 *
 * @remarks
 * The imperative grammar recognizes one sentence mood. Real players type
 * four: commands, questions, statements, and social noise. Before D29 the
 * other three fell into generic failure and were patched one register fix at
 * a time; this module closes the CLASS. The ladder, in order — first shape
 * that speaks, wins:
 *
 *   1. **Social** — every content token is a social word ("hello",
 *      "thanks, ok") → `social_input`.
 *   2. **Polite request** — `<interrogative> you …` ("can you open the
 *      gate") → strip the courtesy prefix and re-parse the remainder through
 *      the REAL pipeline; a command or clarification wins outright. Grammar
 *      stays the judge — no shape-level guessing.
 *   3. **Question** — an interrogative leader opens the clause. If exactly
 *      one catalog entity is named anywhere in it, the question ROUTES to the
 *      information intents: who-family → `ask`, everything else → `inspect`
 *      (both only when the vocabulary actually declares those intents —
 *      shape routing invents nothing). Several entities → a which-entity
 *      clarification. None → `question_not_understood`.
 *   4. **Direction with resolved chatter** (D31) — a bearing followed by a
 *      phrase that resolves to one catalog entity ("descend stairs") keeps
 *      the MOVEMENT: the bearing is explicit, the noun is destination color.
 *   5. **Statement** — a statement leader opens the clause ("i am tired") →
 *      `statement_not_understood`.
 *   6. **Bare noun** (D37, in the parser since D133) — the WHOLE clause
 *      resolves as one noun phrase ("locker eleven", "the intact locker",
 *      "it") → routed onto `inspect`; a tie → a which-entity clarification.
 *      Tried before the unknown verb on purpose: the widest reading first,
 *      so a name is never blamed on its own first word. See
 *      `rescue-bare-noun.ts`.
 *   7. **Unknown verb** (D36) — an unrecognized leading word whose clause
 *      remainder resolves to one catalog entity IS a verb attempt, whatever
 *      the word is ("florb the door", "unscrew the panel") →
 *      `unsupported_action`, carrying the surface and the object so the
 *      engine can refuse by name. This is the rung that covers every verb
 *      without enumerating any.
 *
 * Everything is data-driven (the lexicon's D29 word classes; absent lists
 * disable their rungs), deterministic, and failure-path-only: no clause the
 * grammar can parse is ever touched, so an authored vocabulary always wins
 * over shape opinion.
 *
 * @packageDocumentation
 */

// cspell:ignore florb -- an invented verb: the D36 rung's example of covering every verb without listing any

import { canonicalizeTokens, type CanonicalToken } from '../grammar/canonicalize-tokens.js';
import { type CompiledIntent, type CompiledLexicon } from '../lexicon/compile-lexicon.js';
import { findStrandedEntities } from '../resolve/match-head.js';
import { resolvePronoun } from '../resolve/resolve-pronoun.js';
import {
	type ClarificationCandidate,
	type ClarificationRequest,
	type DirectionToken,
	type EntityRef,
	type ParseCandidateEntity,
	type ParseDiagnostics,
	type Token,
} from '../types.js';
import { rescueBareNoun, type RoutedBareNoun } from './rescue-bare-noun.js';

/** The confidence a shape-routed question command carries (below canonical 1.0, far above the floor). */
export const SHAPE_ROUTED_CONFIDENCE = 0.85;

/**
 * How long an unrecognized word must be before the ladder will call it a verb
 * (D36, lowered by D226/E11). Three: `pet`, `hug`, `pat`, `rub` are the verbs
 * a player types at a dog or a person, and D36's floor of four turned every
 * one of them into a thing's name ("You see no pet dog here."). The floor
 * guarded the mistyped short word ("tke" for "take") — but the fuzzy pass
 * never corrects under four letters, so the guard bought nothing: `tke the
 * dog` became "You see no tke dog here", the same slip in the same mouth,
 * with the dog's name mangled besides. Structure is the better judge at
 * three as at four: the word leads, the rest resolves, so the word is a verb
 * and the refusal names the thing. One- and two-letter fragments stay out —
 * `ge the flask` is nobody's verb.
 */
const MINIMUM_UNKNOWN_VERB_LENGTH = 3;

/** The who-family leaders — the questions that route to `ask` rather than `inspect`. */
const WHO_FAMILY_LEADERS: ReadonlySet<string> = new Set(['who', "who's", 'whom', 'whos', 'whose']);

/** What one rung of the ladder produced (mirrors the orchestrator's clause-result arms structurally). */
export type ShapeRescueResult =
	| {
		/** D231: the one thing the chatter named, kept for the consumer to decide by (`enter door`). */
		readonly destination: ClarificationCandidate;
		readonly direction: DirectionToken;
		readonly type: 'routed_movement';
	}
	| { readonly clarification: ClarificationRequest; readonly type: 'clarification' }
	| { readonly diagnostics: ParseDiagnostics; readonly type: 'diagnostics' }
	| { readonly routed: RoutedBareNoun; readonly type: 'routed_bare_noun' }
	| { readonly routed: RoutedQuestion; readonly type: 'routed_question' }
	| { readonly strippedTokens: readonly Token[]; readonly type: 'reparse_as_request' };

/** What the ladder may be handed beyond the clause itself. */
export interface ShapeRescueOptions {
	/** Whether the polite-request rung may fire (off on the post-courtesy rerun). */
	readonly allowRequestRung?: boolean;
	/**
	 * The clause's canonical tokens, fuzzy-corrected, when the caller already
	 * has them — the bare-noun rung resolves these. Absent, the ladder
	 * canonicalizes the raw tokens itself (no corrections).
	 */
	readonly canonicalTokens?: readonly CanonicalToken[];
	/** Most-recent-first refs, so a bare pronoun resolves as it would after a verb (D67). */
	readonly recentEntityRefs?: readonly EntityRef[];
}

/** A question routed onto an information intent — the orchestrator assembles the command. */
export interface RoutedQuestion {
	/**
	 * The vocabulary's own record for the intent this question routes to (`ask`
	 * or `inspect`). The rung resolved it before it agreed to route at all, so it
	 * travels with the result: an orchestrator that re-looked-it-up by name would
	 * need an undefined-guard on a lookup that cannot miss (engineering_standards
	 * §14.1 — restructure so the invariant is type-visible).
	 */
	readonly intent: CompiledIntent;
	/** Whether the entity binds as the ask subject (indirect object) or the inspect target (direct object). */
	readonly bindsAs: 'direct_object' | 'indirect_object';
	readonly entityRef: EntityRef;
}

/**
 * Try the shape ladder against one failed clause.
 *
 * @param clauseTokens - The clause's tokens as parsed (soft filler already stripped)
 * @param clauseText - The clause, folded (clarifications cite it)
 * @param lexicon - The compiled vocabulary (word classes + declared intents)
 * @param entities - The per-parse catalog
 * @param options - See {@link ShapeRescueOptions}
 * @returns The first rung that speaks, or `undefined` when no shape claims the clause
 */
export const rescueSentenceShape = (
	clauseTokens: readonly Token[],
	clauseText: string,
	lexicon: CompiledLexicon,
	entities: readonly ParseCandidateEntity[],
	options: ShapeRescueOptions = {},
): ShapeRescueResult | undefined => {
	const words = clauseTokens.map((clauseToken) => clauseToken.text);
	const [leadingWord, secondWord] = words;

	if (leadingWord === undefined) {
		return undefined;
	}

	// Rung 1 — social: clauses made entirely of social words (+ stopwords, and
	// "you" as courtesy furniture — "thank you", "no thank you") that OPEN
	// socially. Bare "you" opens nothing and falls through to the statement rung.
	const contentWords = words.filter((word) => !lexicon.stopwordSet.has(word));
	const [firstContentWord] = contentWords;

	if (
		firstContentWord !== undefined &&
		lexicon.socialWordSet.has(firstContentWord) &&
		contentWords.every((word) => lexicon.socialWordSet.has(word) || word === 'you')
	) {
		return {
			diagnostics: { failureKind: 'social_input', unmatchedTokens: words },
			type: 'diagnostics',
		};
	}

	// Rung 2 — polite request: "<interrogative> you <imperative…>" re-parses.
	if (
		options.allowRequestRung !== false &&
		lexicon.interrogativeLeaderSet.has(leadingWord) &&
		secondWord === 'you' &&
		clauseTokens.length > 2
	) {
		return { strippedTokens: clauseTokens.slice(2), type: 'reparse_as_request' };
	}

	// Rung 3 — direction with resolved chatter (D31): "descend stairs",
	// "north gate" — the player named a bearing; a trailing phrase that
	// resolves to a real entity is destination color, not a defect. D231: the
	// thing rides along, because `enter door` names the door and the consumer
	// decides whether the door or the bearing is what moves.
	if (lexicon.directionsByToken.has(leadingWord) && words.length > 1) {
		const [strandedDestination, ...furtherDestinations] = findStrandedEntities(words.slice(1), entities);
		const direction = lexicon.directionsByToken.get(leadingWord);

		if (strandedDestination !== undefined && furtherDestinations.length === 0 && direction !== undefined) {
			return { destination: strandedDestination, direction, type: 'routed_movement' };
		}
	}

	// Rung 4 — question: route to the information intents, ask, or teach.
	if (lexicon.interrogativeLeaderSet.has(leadingWord)) {
		return rescueQuestion(words, clauseText, lexicon, entities);
	}

	// Rung 5 — statement.
	if (lexicon.statementLeaderSet.has(leadingWord)) {
		return {
			diagnostics: { failureKind: 'statement_not_understood', unmatchedTokens: words },
			type: 'diagnostics',
		};
	}

	// Rung 6 — the bare NOUN (D133): the whole clause names one thing. Read
	// BEFORE the unknown verb, because that rung reads token 0 as a verb and
	// resolves only the remainder — the narrower reading — and a name must
	// never be blamed on its own first word.
	const canonicalTokens = options.canonicalTokens ?? canonicalizeTokens(clauseTokens, lexicon);
	const recentEntityRefs = options.recentEntityRefs ?? [];
	const bareNounResult = rescueBareNoun(canonicalTokens, clauseText, lexicon, entities, recentEntityRefs);

	if (bareNounResult !== undefined) {
		return bareNounResult;
	}

	// Rung 7 — the unknown VERB (D36). A word the vocabulary does not know,
	// leading a clause whose remainder names something the catalog DOES know,
	// is a verb: structure says so, whatever the word happens to be. No list
	// can enumerate every verb a player might reach for, so the parser stops
	// trying and reports the attempt instead — the engine refuses it by name.
	return rescueUnknownVerb(words, canonicalTokens, lexicon, entities, recentEntityRefs);
};

/**
 * Report an unrecognized leading word as an attempted verb when the rest of
 * the clause resolves (D36) — a thing the catalog names, or (D229) a pronoun
 * recency answers, so `pet it` is a verb attempt at the thing last spoken
 * of, as `pet the dog` is at the dog.
 *
 * @param words - The clause's surface words
 * @param canonicalTokens - The same clause, canonicalized (a pronoun remainder is read from here)
 * @param lexicon - The compiled vocabulary
 * @param entities - The per-parse catalog
 * @param recentEntityRefs - Most-recent-first refs, for a pronoun remainder
 * @returns The attempt diagnostics, or `undefined` when nothing in the clause resolves
 */
const rescueUnknownVerb = (
	words: readonly string[],
	canonicalTokens: readonly CanonicalToken[],
	lexicon: CompiledLexicon,
	entities: readonly ParseCandidateEntity[],
	recentEntityRefs: readonly EntityRef[],
): ShapeRescueResult | undefined => {
	const [verbSurface, ...remainder] = words;

	if (verbSurface === undefined || remainder.length === 0) {
		return undefined;
	}

	// A word the vocabulary knows in ANY role is not an unknown verb; those
	// clauses failed for a structural reason the other rungs and diagnostics
	// already explain honestly. A word that only OPENS a multiword alias
	// ("blow" of "blow out", "pick" of "pick up"), typed without its tail, is
	// a verb the vocabulary does NOT know on its own — D230 reads it as the
	// attempt it is. Until then it was excused here on the promise of a
	// near-miss teach that nothing supplied, and the clause fell to
	// `no_intent_recognized`, where the engine quoted the whole phrase as a
	// thing's name: `blow candle`, with the candle burning in front of the
	// player, answered "You see no blow candle here." (Salt Debt, Calder's
	// §4). "Nothing about the candle answers to BLOW." is the truth.
	if (
		lexicon.intentsByToken.has(verbSurface) ||
		lexicon.directionsByToken.has(verbSurface) ||
		lexicon.stopwordSet.has(verbSurface) ||
		lexicon.prepositionTokens.has(verbSurface)
	) {
		return undefined;
	}

	// One- and two-letter fragments are not verbs ("ge the flask"): claiming
	// them as actions would put a slip in the narrator's mouth with nothing
	// to show for it. The honest teach owns that case.
	if (verbSurface.length < MINIMUM_UNKNOWN_VERB_LENGTH) {
		return undefined;
	}

	const strandedCandidates = findStrandedEntities(remainder, entities);
	const [soleCandidate] = strandedCandidates;
	// The catalog's own word first (D226, E12: `him` among Sylvain's nouns);
	// a pronoun nobody present claims is recency's.
	const attempted =
		strandedCandidates.length === 1 && soleCandidate !== undefined
			? { entityRef: soleCandidate.entityRef, surface: soleCandidate.matchedTokens.join(' ') }
			: pronounRemainderOf(canonicalTokens, recentEntityRefs, entities);

	if (attempted === undefined) {
		return undefined;
	}

	return {
		diagnostics: {
			attemptedObjectRef: attempted.entityRef,
			// D227: the name as the player typed it, for the refusal to quote (E20's rule, on a failure).
			attemptedObjectSurface: attempted.surface,
			attemptedVerbSurface: verbSurface,
			failureKind: 'unsupported_action',
			unmatchedTokens: words,
		},
		type: 'diagnostics',
	};
};

/**
 * D229: a clause whose remainder is one pronoun, read against recency —
 * `pet it` after `examine the chart` is a verb attempt at the chart.
 *
 * @param canonicalTokens - The clause, canonicalized
 * @param recentEntityRefs - Most-recent-first refs
 * @param entities - The per-parse catalog (an entry's declared pronoun decides what may answer, D245)
 * @returns The thing the pronoun means and the pronoun as typed, or `undefined`
 */
const pronounRemainderOf = (
	canonicalTokens: readonly CanonicalToken[],
	recentEntityRefs: readonly EntityRef[],
	entities: readonly ParseCandidateEntity[],
): { readonly entityRef: EntityRef; readonly surface: string } | undefined => {
	const [, pronounToken, ...rest] = canonicalTokens;

	if (pronounToken?.kind !== 'pronoun' || rest.length > 0) {
		return undefined;
	}

	const resolved = resolvePronoun(pronounToken, recentEntityRefs, entities);

	return resolved.type === 'unresolved' ? undefined : { entityRef: resolved.resolution.entityRef, surface: pronounToken.sourceText };
};

/**
 * Resolve a question clause: entity named → routed information intent;
 * several → clarification; none → the question teach.
 *
 * @param words - The clause's surface words (leader included)
 * @param clauseText - The clause, folded
 * @param lexicon - The compiled vocabulary
 * @param entities - The per-parse catalog
 * @returns The rung's result
 */
const rescueQuestion = (
	words: readonly string[],
	clauseText: string,
	lexicon: CompiledLexicon,
	entities: readonly ParseCandidateEntity[],
): ShapeRescueResult => {
	const [leadingWord] = words;
	const strandedCandidates = findStrandedEntities(words.slice(1), entities);
	const intentText = leadingWord !== undefined && WHO_FAMILY_LEADERS.has(leadingWord) ? 'ask' : 'inspect';

	// Shape routing invents nothing: the vocabulary must declare the intent. The
	// record resolved here is what the routed result carries, so the intent is
	// looked up exactly once on this path.
	const routedIntent = lexicon.intentsByToken.get(intentText);

	if (strandedCandidates.length > 0 && routedIntent !== undefined) {
		const [soleCandidate] = strandedCandidates;

		if (strandedCandidates.length === 1 && soleCandidate !== undefined) {
			return {
				routed: {
					bindsAs: intentText === 'ask' ? 'indirect_object' : 'direct_object',
					entityRef: soleCandidate.entityRef,
					intent: routedIntent,
				},
				type: 'routed_question',
			};
		}

		return {
			clarification: { candidates: strandedCandidates, clauseText, kind: 'which_entity' },
			type: 'clarification',
		};
	}

	return {
		diagnostics: { failureKind: 'question_not_understood', unmatchedTokens: words },
		type: 'diagnostics',
	};
};
